/** Chat avatar: picture when available, initials otherwise, with the
 *  Messenger-style status bubble. When lastActive (ISO, from the silent
 *  interaction tracker) is available: green dot = active in the last 5 min,
 *  otherwise a grey "Nm" bubble that counts up each minute away. Falls back
 *  to the plain online/offline dot when no activity data exists. */
import { useEffect, useReducer } from "react";

const COLORS = ["#1a3fd1", "#0e7a5f", "#b3541e", "#7c3aed", "#be185d", "#0369a1"];

export function minutesAway(lastActive) {
  if (!lastActive) return null;
  const t = new Date(lastActive).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}

export default function Avatar({ name = "", picture, online, lastActive, size = 40, showStatus = true, testId }) {
  const [, tick] = useReducer((x) => x + 1, 0);
  const away = minutesAway(lastActive);
  const live = away !== null && away < 5;

  // re-render once a minute so the "Nm" bubble counts up on its own
  useEffect(() => {
    if (away === null || live) return undefined;
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [away === null, live]); // eslint-disable-line react-hooks/exhaustive-deps

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";
  const color = COLORS[(name.charCodeAt(0) || 0) % COLORS.length];
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testId}>
      {picture ? (
        <img src={picture} alt={name} className="h-full w-full rounded-full object-cover" />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center rounded-full font-semibold text-white"
          style={{ background: color, fontSize: size * 0.4 }}
        >
          {initials}
        </div>
      )}
      {showStatus && (
        away !== null ? (
          live ? (
            <span
              className="absolute bottom-0 right-0 block rounded-full bg-emerald-500 ring-2 ring-white"
              style={{ width: Math.max(10, size * 0.28), height: Math.max(10, size * 0.28) }}
              title="Active now"
              data-testid={testId ? `${testId}-status` : undefined}
            />
          ) : (
            <span
              className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-gray-400 px-1 font-semibold text-white ring-2 ring-white"
              style={{ height: Math.max(15, size * 0.4), minWidth: Math.max(15, size * 0.4), fontSize: Math.max(8, size * 0.22) }}
              title={`Active ${away}m ago`}
              data-testid={testId ? `${testId}-status` : undefined}
            >
              {away}m
            </span>
          )
        ) : (
          <span
            className={`absolute bottom-0 right-0 block rounded-full ring-2 ring-white ${online ? "bg-emerald-500" : "bg-red-400"}`}
            style={{ width: Math.max(10, size * 0.28), height: Math.max(10, size * 0.28) }}
            title={online ? "Online" : "Offline"}
            data-testid={testId ? `${testId}-status` : undefined}
          />
        )
      )}
    </div>
  );
}
